import { useState, useEffect } from "react";
import type { AssignmentCriterion, CriteriaCategory, CriteriaTemplateItem } from "@markeroo/shared";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { Tooltip } from "@/components/Tooltip";
import { AutoResizeTextarea } from "@/components/AutoResizeTextarea";
import { CategorySelector } from "@/components/CategorySelector";
import { fieldLabelClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { useCreateCriterion, useUpdateCriterion } from "@/api/hooks";
import { useNotification } from "@/providers/NotificationProvider";
import { notifications, ui, placeholders } from "@/strings";
import type { NotificationKind } from "@/providers/NotificationProvider";

/**
 * Common interface for criterion data that works with both
 * AssignmentCriterion and CriteriaTemplateItem types.
 */
export interface CriterionEditData {
  key?: string;
  label: string;
  humanDescription: string;
  llmDescription: string;
  category?: CriteriaCategory;
}

interface CriteriaEditModalProps {
  isOpen: boolean;
  criterion: AssignmentCriterion | CriteriaTemplateItem | null;
  assignmentId?: string; // optional - only needed for assignment criteria
  onClose: () => void;
  // Callback-based save for criteria templates or custom handling
  onSave?: (data: CriterionEditData) => void;
  isSaving?: boolean;
}

export function CriteriaEditModal({
  isOpen,
  criterion,
  assignmentId,
  onClose,
  onSave,
  isSaving: isSavingOverride,
}: CriteriaEditModalProps) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [llmDescription, setLlmDescription] = useState("");
  const [category, setCategory] = useState<CriteriaCategory>("other");

  const createMutation = assignmentId
    ? useCreateCriterion(assignmentId)
    : ({ mutateAsync: async () => {}, isPending: false } as const);
  const updateMutation = assignmentId
    ? useUpdateCriterion(assignmentId)
    : ({ mutateAsync: async () => {}, isPending: false } as const);
  const { show } = useNotification();
  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_ERROR: NotificationKind = "error";

  const isExisting = Boolean(criterion && criterion.key);
  const isSaving = isSavingOverride ?? (createMutation.isPending || updateMutation.isPending);

  // Helper to extract human description from either type
  // AssignmentCriterion uses "description", CriteriaTemplateItem uses "humanDescription"
  const getHumanDescription = (c: AssignmentCriterion | CriteriaTemplateItem): string => {
    if ("humanDescription" in c) {
      return c.humanDescription ?? "";
    }
    return c.description ?? "";
  };

  useEffect(() => {
    if (criterion) {
      setLabel(criterion.label);
      setDescription(getHumanDescription(criterion));
      setLlmDescription(criterion.llmDescription ?? "");
      setCategory(criterion.category ?? "other");
    } else {
      setLabel("");
      setDescription("");
      setLlmDescription("");
      setCategory("other");
    }
  }, [criterion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;

    // If an external onSave callback is provided, use it (for criteria templates or custom handling)
    if (onSave) {
      onSave({
        key: criterion?.key,
        label,
        humanDescription: description,
        llmDescription,
        category,
      });
      onClose();
      return;
    }

    try {
      if (!assignmentId) throw new Error("assignmentId is required");
      if (isExisting && criterion) {
        await updateMutation.mutateAsync({
          criterionKey: criterion.key,
          label,
          llmDescription,
          humanDescription: description,
          category,
        });
        show(notifications.criteria.ruleUpdated, TOAST_SUCCESS);
      } else {
        await createMutation.mutateAsync({
          label,
          llmDescription,
          humanDescription: description,
          category,
        });
        show(notifications.criteria.ruleCreated, TOAST_SUCCESS);
      }
      onClose();
    } catch {
      show(notifications.criteria.ruleSaveFailed, TOAST_ERROR);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <form onSubmit={(e) => { void handleSubmit(e); }}>
        <div className="mb-3">
          <ModalHeader
            title={
              isExisting
                ? `${ui.actions.edit} ${criterion?.label || ui.labels.criterion}`
                : `${ui.actions.add} ${ui.labels.criterion}`
            }
            onClose={handleClose}
          />
        </div>
        <ModalBody>
          <div className="flex flex-col gap-4 px-2 pb-4">
            <label className={fieldLabelClass}>
              <span className="flex items-center gap-1">
                {ui.labels.name}
                <Tooltip content={ui.labels.criterionNameHelp} />
              </span>
              <input
                type="text"
                className="rounded-lg border border-border bg-white px-3 py-2 text-base text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
                autoFocus
              />
            </label>

            <CategorySelector
              selectedCategory={category}
              onCategoryChange={setCategory}
            />

            <label className={fieldLabelClass}>
              <span className="flex items-center gap-1 text-theme-secondary">
                {ui.labels.studentFeedbackTemplate}
                <Tooltip content={ui.labels.criterionStudentHelp} />
              </span>
              <AutoResizeTextarea
                className="rounded-lg border border-border bg-white px-3 py-2 text-base text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                minHeight={96}
                maxHeight={320}
                placeholder={placeholders.criteria.humanDescription}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </label>

            <label className={fieldLabelClass}>
              <span className="flex items-center gap-1 text-theme-secondary">
                {ui.labels.llmInstructions}
                <Tooltip content={ui.labels.criterionLLMHelp} />
              </span>
              <AutoResizeTextarea
                className="rounded-lg border border-border bg-white px-3 py-2 text-base text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                minHeight={96}
                maxHeight={320}
                placeholder={placeholders.criteria.llmDescription}
                value={llmDescription}
                onChange={(e) => setLlmDescription(e.target.value)}
                rows={4}
              />
            </label>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className={buttonVariants.secondary}
            onClick={handleClose}
            disabled={isSaving}
          >
            {ui.actions.cancel}
          </button>
          <button
            type="submit"
            className={buttonVariants.primary}
            disabled={isSaving || !label.trim()}
          >
            {isSaving ? ui.actions.saving : ui.actions.save}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
